import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { authApi } from '@/api/auth'
import { Alert } from '@/components/ui/Alert'
import { Spinner } from '@/components/ui/Spinner'

type Status = 'pending' | 'success' | 'invalid' | 'missing_token'

/** Message associé à chaque issue de la vérification. */
const MESSAGE_KEYS: Record<Exclude<Status, 'pending'>, string> = {
  success: 'auth.verify_success',
  invalid: 'auth.verify_invalid',
  missing_token: 'auth.verify_missing_token',
}

export function VerifyEmailPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  // L'absence de jeton est connue dès le premier rendu : inutile de passer par
  // un effet, qui déclencherait un rendu en cascade.
  const [status, setStatus] = useState<Status>(token ? 'pending' : 'missing_token')

  useEffect(() => {
    if (!token) return

    authApi
      .verifyEmail(token)
      .then(() => setStatus('success'))
      .catch(() => setStatus('invalid'))
  }, [token])

  return (
    <main className="min-h-screen bg-surface-50 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <h1 className="text-2xl font-semibold text-surface-900 mb-8 text-center">
          {t('auth.verify_title')}
        </h1>

        {status === 'pending' && (
          <div className="flex justify-center">
            <Spinner size="lg" />
          </div>
        )}

        {status === 'success' && (
          <Alert variant="success">{t(MESSAGE_KEYS.success)}</Alert>
        )}

        {(status === 'invalid' || status === 'missing_token') && (
          <Alert variant="error">{t(MESSAGE_KEYS[status])}</Alert>
        )}
      </div>
    </main>
  )
}
